import React, { useEffect, PropsWithChildren, useState } from 'react';
import { AnalyticsController } from 'analytics/analytics-controller';
import CommercialHeader from 'components/header/commercial';
import LoadingSpinner from 'components/loading-spinner';
import { useFeaturesContext } from 'contexts/features';
import styles from '../style.module.scss';
import { WebVitals } from 'components/web-vitals';
import { useRouterLoading } from 'hooks/use-router-loading';
import CommercialFooter from 'components/footer/exp/commercial/commercialFooter';
import CommercialFooterV2 from 'components/footer/exp/commercial/commercialFooterV2';
import { getTenantById } from '@tenants/manager';
import { getTenantFooterConfig, getTenantOrganizationConfig } from '@tenants/utils/configs';
import { LOCALE_EXP_COMMERCIAL_PATHS } from 'types/locales';
import { useThemeContext } from 'contexts';

type ExpCommercialLayoutProps = PropsWithChildren<{
  hideHeader?: boolean;
  hideFooter?: boolean;
}>;

export default function ExpCommercialLayout({
  children,
  hideHeader = false,
  hideFooter = false,
}: ExpCommercialLayoutProps) {
  const { tenantId } = useThemeContext();
  const { isLoading } = useRouterLoading();
  const { areDelayedScriptsLoaded } = useFeaturesContext();
  const tenant = getTenantById(tenantId);
  const footerConfig = getTenantFooterConfig(tenant);
  const organizationConfig = getTenantOrganizationConfig(tenant);
  const [useNewExpCommercialFooter, setUseNewExpCommercialFooter] = useState(undefined);

  useEffect(() => {
    if (areDelayedScriptsLoaded) {
      AnalyticsController?.trackDelayedPageView();
    }
  }, [areDelayedScriptsLoaded]);

  // TODO: cleanup this logic when we go live with expCommercial
  useEffect(() => {
    const { hostname } = window.location;
    if (hostname !== 'legacy.expcommercial.com') {
      setUseNewExpCommercialFooter(true);
      return;
    }
    if (footerConfig?.footerColumns) {
      setUseNewExpCommercialFooter(false);
      const { column1, column2 } = footerConfig?.footerColumns ?? {};
      if (column1) {
        Object.assign(column1[0], {
          link: '/services',
        });
        Object.assign(column1[1], {
          label: {
            key: LOCALE_EXP_COMMERCIAL_PATHS.FOOTER_FINDEXPERT_LABEL,
          },
        });
        Object.assign(column1[3], {
          link: '/become-an-advisor',
        });
      }
      if (column2) {
        Object.assign(column2[0], {
          link: '/about',
        });
      }
    }
  }, [footerConfig]);

  return (
    <>
      <WebVitals />
      <CommercialHeader hideHeader={hideHeader} />
      {isLoading && (
        <div className={styles.loading}>
          <LoadingSpinner />
        </div>
      )}
      {!isLoading && (
        <div className={styles.page}>
          {children}
        </div>
      )}
      {!hideFooter && (
        useNewExpCommercialFooter === undefined ? null : useNewExpCommercialFooter ?
          <CommercialFooterV2 config={footerConfig} organizationConfig={organizationConfig} /> :
          <CommercialFooter config={footerConfig} organizationConfig={organizationConfig} />
      )
      }
    </>
  );
}
